import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { EntityTypeActivityList } from "@/features/entity-types/components/entity-type-activity-list";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { assetQueryOptions } from "@/services/asset/asset.options";
import type { AssetTypeDisplay } from "@/services/asset/asset.types";

export interface AssetTypeActivitySectionProps {
	assetType: AssetTypeDisplay;
	/** Kept so drawer and page callers can pass the surface they render on. */
	variant?: "drawer" | "full";
	/** Organization ID to include in API headers. Pass EMPTY_ORG_ID for platform-level calls. */
	organizationId?: string;
}

export function AssetTypeActivitySection({
	assetType,
	organizationId,
}: AssetTypeActivitySectionProps) {
	const { t } = useAppTranslation();
	const pageSize = 10;

	const historyCountQuery = useQuery(
		assetQueryOptions.assetTypeHistory.count(assetType.id, organizationId),
	);
	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useInfiniteQuery(
			assetQueryOptions.assetTypeHistory.infinite(
				assetType.id,
				pageSize,
				historyCountQuery.data ?? 0,
				organizationId,
			),
		);

	return (
		<EntityTypeActivityList
			pages={data?.pages}
			isLoading={isLoading}
			entityLabel={t("admin.entityTypes.access.entity.asset")}
			emptyTitle={t("admin.assetTypes.drawer.activity.history.empty")}
			emptyDescription={t(
				"admin.assetTypes.drawer.activity.history.description",
			)}
			ariaLabel={t("admin.assetTypes.drawer.activity.history.title")}
			hasNextPage={hasNextPage ?? false}
			isFetchingNextPage={isFetchingNextPage}
			fetchNextPage={() => {
				void fetchNextPage();
			}}
		/>
	);
}
